import React, { useState } from 'react';
import './BudgetTable.css';

const BudgetTable = ({ items, onAddItem, onUpdateItem, onDeleteItem, type }) => {
  const [editingCell, setEditingCell] = useState(null);
  
  // 編集モードを開始
  const startEditing = (id, field) => {
    setEditingCell({ id, field });
  };
  
  // 編集を終了
  const finishEditing = () => {
    setEditingCell(null);
  };
  
  // 頻度のオプション
  const frequencyOptions = [
    { value: 'day', label: '日' },
    { value: 'week', label: '週' },
    { value: 'month', label: '月' },
    { value: 'year', label: '年' },
    { value: 'biennial', label: '2年に1度' }
  ];
  
  // 頻度の表示ラベルを取得
  const getFrequencyLabel = (frequency) => {
    const option = frequencyOptions.find(opt => opt.value === frequency);
    return option ? option.label : '';
  };
  
  // 頻度の表示用フルラベルを取得
  const getFullFrequencyLabel = (frequency) => {
    const labels = {
      'day': '毎日',
      'week': '毎週',
      'month': '毎月',
      'year': '毎年',
      'biennial': '2年に1度'
    };
    return labels[frequency] || '';
  };
  
  // 通貨フォーマット
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  // キー操作のハンドラー
  const handleKeyDown = (e, id, field, nextField) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finishEditing();
      
      if (nextField) {
        // 次のフィールドがある場合は次のフィールドに移動
        setTimeout(() => startEditing(id, nextField), 10);
      }
    } else if (e.key === 'Tab') {
      finishEditing();
    }
  };
  
  // 入力値の処理
  const handleContentChange = (id, value) => {
    onUpdateItem(id, 'content', value);
  };
  
  const handleFrequencyChange = (id, value) => {
    onUpdateItem(id, 'frequency', value);
  };
  
  const handleAmountChange = (id, value) => {
    // カンマを除去して数値のみ抽出
    let numericValue = value.replace(/[^\d]/g, '');
    onUpdateItem(id, 'amount', numericValue ? parseInt(numericValue) : 0);
  };
  
  // 金額を3桁区切りでフォーマット（編集中用）
  const formatAmountForEditing = (amount) => {
    if (!amount || amount === 0) return '';
    return parseInt(amount).toLocaleString();
  };
  
  return (
    <div className={`budget-table ${type}`}>
      <table>
        <thead>
          <tr>
            <th className="content-col">内容</th>
            <th className="frequency-col">頻度</th>
            <th className="amount-col">金額</th>
            <th className="action-col"></th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              {/* 内容セル */}
              <td 
                className="content-cell"
                onClick={() => startEditing(item.id, 'content')}
              >
                {editingCell && editingCell.id === item.id && editingCell.field === 'content' ? (
                  <input
                    type="text"
                    value={item.content}
                    onChange={(e) => handleContentChange(item.id, e.target.value)}
                    onBlur={finishEditing}
                    onKeyDown={(e) => handleKeyDown(e, item.id, 'content', 'frequency')}
                    autoFocus
                  />
                ) : (
                  <span className={!item.content ? 'placeholder' : ''}>
                    {item.content || '内容を入力...'}
                  </span>
                )}
              </td>
              
              {/* 頻度セル */}
              <td className="frequency-cell">
                {editingCell && editingCell.id === item.id && editingCell.field === 'frequency' ? (
                  <div className="frequency-selector">
                    {frequencyOptions.map(option => (
                      <button
                        key={option.value}
                        className={`frequency-option ${item.frequency === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          handleFrequencyChange(item.id, option.value);
                          finishEditing();
                          setTimeout(() => startEditing(item.id, 'amount'), 10);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <span
                    onClick={() => startEditing(item.id, 'frequency')}
                    className="frequency-display"
                  >
                    {getFullFrequencyLabel(item.frequency)}
                  </span>
                )}
              </td>
              
              {/* 金額セル */}
              <td 
                className="amount-cell"
                onClick={() => startEditing(item.id, 'amount')}
              >
                {editingCell && editingCell.id === item.id && editingCell.field === 'amount' ? (
                  <input
                    type="text"
                    value={formatAmountForEditing(item.amount)}
                    onChange={(e) => handleAmountChange(item.id, e.target.value)}
                    onBlur={finishEditing}
                    onKeyDown={(e) => handleKeyDown(e, item.id, 'amount', null)}
                    autoFocus
                    inputMode="numeric"
                  />
                ) : (
                  <span className="amount">
                    <span className="currency-symbol">¥</span>
                    <span className="amount-value">
                      {parseInt(item.amount || 0).toLocaleString()}
                    </span>
                  </span>
                )}
              </td>
              
              {/* アクションセル */}
              <td className="action-cell">
                <button
                  className="delete-btn"
                  onClick={() => onDeleteItem(item.id)}
                  aria-label="削除"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
          
          {/* 新規追加ボタン行 */}
          <tr className="add-row">
            <td colSpan="4">
              <button className="add-btn" onClick={onAddItem}>
                + 追加
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default BudgetTable; 